import styled from '@emotion/styled';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import img from 'src/public/img/link-expired.svg';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { Http } from 'src/shared/services/Http';
import { color, font } from 'src/shared/styles/variables';

interface Props {
    token: string;
}

const ContentWrapper = styled.div({
    padding: '56px',
    display: 'flex',
    textAlign: 'center',
    flexDirection: 'column',
    alignItems: 'center',
});

const HeadingText = styled.h2(
    {
        color: color.ebonyClay,
        maxWidth: '320px',
    },
    font.heavy(font.size.header),
);

const DescriptionText = styled.p(
    {
        maxWidth: '460px',
        paddingBottom: '52px',
        color: color.blueBayoux,
    },
    font.medium(font.size.medium),
);

const Link = styled('a')(
    {
        cursor: 'pointer',
        color: color.royalBlue,
        '&:hover': {
            textDecoration: 'underline',
            color: color.royalBlue,
        },
    },
    font.medium(font.size.medium),
);

const StyledParagraph = styled('p')(
    {
        color: color.blueBayoux,
    },
    font.medium(font.size.medium),
);

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

export const RegistrationLinkNotValidPage: React.FC = () => {
    const { t } = useTranslation();
    const helpCenterUrl = t('user:help_center.complete_url');
    const [isEmailResent, setIsEmailResent] = useState(false);
    const location = useLocation<Props>();
    const token = location?.state?.token;
    const hitTokenApiEndpoint = async () => {
        const http: Http = new Http();
        setIsEmailResent(true);
        const url = `accounts/check-signuptoken/${token}`;
        await http.GET_UNAUTHENTICATED<string>(url).catch(err => {
            if (err.body.detail !== 'Token expired') {
                toastNotificationsService.renderErrorToast(config.messages.genericError);
            }
        });
    };

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = 'https://www.modernhealth.com';
                }}
            />
            <ContentWrapper>
                <img src={img} />
                <HeadingText>{t('user:registration.link_not_valid.title')}</HeadingText>
                <DescriptionText>
                    {t('user:registration.link_not_valid.description')}
                </DescriptionText>
                {!isEmailResent && (
                    <Trans ns="user" i18nKey="registration.link_not_valid.actions">
                        <StyledParagraph>Didn't get the email?</StyledParagraph>
                        <Link onClick={() => hitTokenApiEndpoint()}>Resend email</Link>
                    </Trans>
                )}
                {isEmailResent && (
                    <Trans ns="user" i18nKey="registration.link_not_valid.actions.help">
                        <StyledParagraph>We sent another verification email.</StyledParagraph>
                        <Link onClick={() => window.open(helpCenterUrl, '_blank')}>Need help?</Link>
                    </Trans>
                )}
            </ContentWrapper>
        </>
    );
};
